// Thin API client. Calls the control-plane api same-origin under /api (nginx proxies /api -> the
// api service in prod; Vite proxies it in dev). Auth is the Google-login SESSION COOKIE, sent
// automatically via credentials:"include". The operator plane is ROLE-BASED — there is no key in
// the browser; the admin key is a programmatic/CLI break-glass only, never entered here.

// An operator can choose to view its OWN tenant as a client. When that view is active we send
// `X-Amendie-View: client` on every request; the server de-elevates for that request (admin=false,
// tenant-scoped). Persisted so the choice survives a reload. Default (absent) = operator/native view.
const VIEW_STORAGE = "amendie_view";
export function getView(): "client" | "operator" {
  return localStorage.getItem(VIEW_STORAGE) === "client" ? "client" : "operator";
}
export function setView(v: "client" | "operator"): void {
  if (v === "client") localStorage.setItem(VIEW_STORAGE, "client");
  else localStorage.removeItem(VIEW_STORAGE);
}
function viewHeaders(): Record<string, string> {
  return getView() === "client" ? { "x-amendie-view": "client" } : {};
}

export type TenantRole = "owner" | "admin" | "member" | "viewer";

/** Current session identity (Google login). */
export interface Me {
  tenantId: string;
  userId: string | null;
  /** Elevated to the platform plane RIGHT NOW (operator identity AND not viewing as a client). */
  admin: boolean;
  /** The account CAN be an operator (identity) — drives whether the view toggle is shown. */
  operator: boolean;
  /** Per-tenant role for a customer session; null for the keyless break-glass admin. */
  tenantRole: TenantRole | null;
  // Profile — present only for a signed-in user (a keyless break-glass admin has no person).
  email?: string | null;
  /** Display name; falls back to email in the UI. */
  name?: string | null;
  /** Ready-to-use <img> src (uploaded avatar proxied by the api, or an external photo). Null = none. */
  avatar?: string | null;
}

/**
 * Resolve the caller's identity from the session cookie. `null` if not signed in; the `"maintenance"`
 * sentinel when the platform is in FULL LOCKDOWN and this (non-operator) caller is blocked — the auth hook
 * 503s every request with `{error:"maintenance"}`, so the app shows a maintenance page instead of Landing.
 */
export type MeResult = Me | null | "maintenance";
export async function getMe(): Promise<MeResult> {
  try {
    const res = await fetch(`/api/auth/me`, { credentials: "include", headers: viewHeaders() });
    if (res.status === 503) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      return body?.error === "maintenance" ? "maintenance" : null;
    }
    if (!res.ok) return null;
    return (await res.json()) as Me;
  } catch {
    return null;
  }
}

/** Clear the browser session (Google login). */
export async function logout(): Promise<void> {
  await fetch(`/api/auth/logout`, { method: "POST", credentials: "include" }).catch(() => undefined);
}

/** The two maintenance flags, from the anonymous PUBLIC endpoint — drives the landing banner. */
export interface PublicMaintenance {
  maintenanceMode: boolean;
  fullLockdown: boolean;
}

/** Read the public maintenance flags. Never throws — a failure resolves to "not in maintenance" so a
 *  transient error can't wrongly show a scary banner (or hide a real one is the lesser risk). */
export async function getPublicMaintenance(): Promise<PublicMaintenance> {
  try {
    const res = await fetch(`/api/public/maintenance`, { credentials: "include" });
    if (!res.ok) return { maintenanceMode: false, fullLockdown: false };
    return (await res.json()) as PublicMaintenance;
  } catch {
    return { maintenanceMode: false, fullLockdown: false };
  }
}

/** Submit a "request access" while a maintenance tier is on. Throws ApiError on 429 / bad input. */
export async function requestAccess(email: string, note?: string): Promise<void> {
  await api("/public/access-request", {
    method: "POST",
    body: JSON.stringify({ email, note: note && note.trim() !== "" ? note.trim() : undefined }),
  });
}

/** One pending access request (operator triage). */
export interface AccessRequest {
  id: string;
  email: string;
  note: string | null;
  createdAt: string;
}
export function listAccessRequests(): Promise<{ pending: AccessRequest[] }> {
  return api<{ pending: AccessRequest[] }>("/settings/access-requests");
}
export async function inviteAccessRequest(id: string): Promise<void> {
  await api(`/settings/access-requests/${id}/invite`, { method: "POST" });
}
export async function dismissAccessRequest(id: string): Promise<void> {
  await api(`/settings/access-requests/${id}/dismiss`, { method: "POST" });
}

/** Submit in-app feedback from the floating widget. `page` is the current app path (context for ops). */
export async function sendFeedback(message: string, page: string): Promise<void> {
  await api("/feedback", { method: "POST", body: JSON.stringify({ message, page }) });
}

/** One feedback note (operator triage). */
export interface FeedbackItem {
  id: string;
  email: string | null;
  workspaceName: string | null;
  message: string;
  page: string | null;
  status: "new" | "handled";
  createdAt: string;
  handledAt: string | null;
  handledBy: string | null;
}
export function listFeedback(): Promise<{ items: FeedbackItem[]; newCount: number }> {
  return api<{ items: FeedbackItem[]; newCount: number }>("/settings/feedback");
}
export async function handleFeedback(id: string): Promise<void> {
  await api(`/settings/feedback/${id}/handle`, { method: "POST" });
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Build an ApiError from a non-2xx response, surfacing the human `message` from the JSON error
 * envelope ({ error, message }) instead of dumping the raw body — every route returns that shape.
 * Falls back to the raw text, then the status text.
 */
async function toApiError(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => "");
  let message = text || res.statusText;
  if (text) {
    try {
      const body = JSON.parse(text) as { message?: unknown; error?: unknown };
      if (typeof body.message === "string") message = body.message;
      else if (typeof body.error === "string") message = body.error;
    } catch {
      /* not JSON — keep the raw text */
    }
  }
  return new ApiError(res.status, message);
}

/**
 * Upload an avatar image. Sends the raw file bytes (not JSON, not multipart) with the file's own
 * content type — the api registers a Buffer parser for image/*. Returns the new avatar src.
 */
export async function uploadAvatar(file: File): Promise<{ avatar: string | null }> {
  const res = await fetch(`/api/session/avatar`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": file.type, ...viewHeaders() },
    body: file,
  });
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as { avatar: string | null };
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const hasBody = init?.body != null;
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...viewHeaders(),
      ...(hasBody ? { "content-type": "application/json" } : {}),
    },
  });
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Fetch a text body — e.g. an HTML snapshot rendered in a sandboxed iframe. */
export async function apiText(path: string): Promise<string> {
  const res = await fetch(`/api${path}`, { credentials: "include", headers: viewHeaders() });
  if (!res.ok) throw await toApiError(res);
  return res.text();
}

/** Fetch a file and trigger a browser download — for CSV/JSON result export. */
export async function download(path: string, filename: string): Promise<void> {
  const res = await fetch(`/api${path}`, { credentials: "include", headers: viewHeaders() });
  if (!res.ok) throw await toApiError(res);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
